import { useEffect, useState } from "react";
import axios from "axios";
import { Phone, Mail, MapPin, Clock, ArrowUpRight } from "lucide-react";
import { API } from "@/App";
import { MaskedLine, FadeUp, Eyebrow } from "@/components/Reveal";
import { Vine, FloatLeaf } from "@/components/Botanical";
import PhotoMarquee from "@/components/PhotoMarquee";
import { BOOKING_URL } from "@/components/Nav";

const SALON_FB = {
  address: "Salon Orgænics, VJ Yashwin Orrizonte, near Panchshil Towers, Kharadi, Pune 411014",
  phone: "+91 91580 00424",
  phone_raw: "919158000424",
  email: "admin.saloneur@gmail.com",
  hours: "11:00 AM – 10:00 PM",
};
const MAP_URL =
  "https://www.google.com/maps?q=VJ+Yashwin+Orrizonte,+near+Panchshil+Towers,+Kharadi,+Pune+411014&output=embed";

export default function Contact() {
  const [salon, setSalon] = useState(SALON_FB);
  useEffect(() => {
    axios
      .get(`${API}/salon`)
      .then((r) => setSalon({ ...SALON_FB, ...r.data }))
      .catch(() => {});
  }, []);
  const whatsapp = `https://wa.me/${salon.phone_raw}?text=Hello%20Salon%20Org%C3%A6nics%2C%20I%27d%20like%20to%20book%20an%20appointment.`;
  return (
    <div data-testid="contact-page">
      <section className="relative min-h-[55vh] flex items-end bg-[#F9FAF8] overflow-hidden pt-36 pb-14">
        <Vine className="absolute right-8 top-24 h-[55%] w-auto opacity-30" flip />
        <FloatLeaf className="w-24 left-[16%] top-[24%] opacity-25" delay={0.3} />
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 w-full relative z-10">
          <MaskedLine onLoad className="mb-6">
            <Eyebrow>Find us · Kharadi, Pune</Eyebrow>
          </MaskedLine>
          <h1 className="font-display font-light leading-[0.95] tracking-tight text-[#0A0A0A]">
            <MaskedLine onLoad delay={0.15} className="text-[13vw] md:text-8xl">
              <span>Come</span>
            </MaskedLine>
            <MaskedLine onLoad delay={0.3} className="text-[13vw] md:text-8xl">
              <span className="italic text-[#11261A]">say hello.</span>
            </MaskedLine>
          </h1>
        </div>
      </section>

      <section className="bg-white py-14 md:py-20" data-testid="contact-details-section">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 grid lg:grid-cols-12 gap-14">
          <div className="lg:col-span-5 space-y-10">
            <FadeUp>
              <div>
                <p className="flex items-center gap-3 text-[11px] uppercase tracking-[0.3em] text-[#8EA696] mb-4">
                  <MapPin size={14} /> The address
                </p>
                <p className="font-display font-light text-2xl md:text-3xl leading-snug text-[#0A0A0A]" data-testid="contact-address">
                  {salon.address}
                </p>
              </div>
            </FadeUp>

            <FadeUp delay={0.1}>
              <div>
                <p className="flex items-center gap-3 text-[11px] uppercase tracking-[0.3em] text-[#8EA696] mb-4">
                  <Clock size={14} /> The hours
                </p>
                <p className="font-display font-light text-2xl md:text-3xl text-[#0A0A0A]" data-testid="contact-hours">
                  {salon.hours}
                </p>
                <p className="text-sm text-[#4A4A4A] mt-2">Open every single day of the week.</p>
              </div>
            </FadeUp>

            <FadeUp delay={0.2}>
              <div className="flex flex-col gap-4 pt-4">
                <a
                  href={`tel:+${salon.phone_raw}`}
                  data-testid="contact-call-button"
                  className="group flex items-center justify-between border border-[#E5E7E3] px-6 py-5 transition-all duration-300 hover:border-[#11261A] hover:bg-[#F9FAF8]"
                >
                  <span className="flex items-center gap-4">
                    <Phone size={18} className="text-[#1B3B28]" />
                    <span>
                      <span className="block text-[10px] uppercase tracking-[0.3em] text-[#8EA696]">Call us</span>
                      <span className="text-base text-[#0A0A0A]">{salon.phone}</span>
                    </span>
                  </span>
                  <ArrowUpRight size={18} className="text-[#11261A] transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" />
                </a>
                <a
                  href={whatsapp}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="contact-whatsapp-button"
                  className="group flex items-center justify-between bg-[#11261A] text-[#F9FAF8] px-6 py-5 transition-all duration-300 hover:bg-black hover:shadow-lg"
                >
                  <span className="flex items-center gap-4">
                    <svg viewBox="0 0 24 24" className="w-[18px] h-[18px] fill-current" aria-hidden="true">
                      <path d="M12 2a10 10 0 0 0-8.6 15.1L2 22l5-1.3A10 10 0 1 0 12 2Zm5.5 14.2c-.2.7-1.3 1.3-1.9 1.3-.5.1-1.1.1-1.8-.1-2.7-.9-4.5-3-5.7-4.9-.5-.9-.9-2-.6-2.9.2-.7 1-1.3 1.4-1.3h.5c.2 0 .4 0 .6.5l.8 2c.1.2.1.4 0 .6l-.5.7c-.1.2-.2.3-.1.5.6 1.1 1.7 2.1 2.9 2.7.2.1.4.1.5-.1l.7-.8c.2-.2.4-.2.6-.1l1.9.9c.3.2.5.3.5.5 0 .1 0 .3-.2.5Z" />
                    </svg>
                    <span>
                      <span className="block text-[10px] uppercase tracking-[0.3em] text-[#8EA696]">WhatsApp</span>
                      <span className="text-base">Message us anytime</span>
                    </span>
                  </span>
                  <ArrowUpRight size={18} className="transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" />
                </a>
                <a
                  href={`mailto:${salon.email}`}
                  data-testid="contact-email-button"
                  className="group flex items-center justify-between border border-[#E5E7E3] px-6 py-5 transition-all duration-300 hover:border-[#11261A] hover:bg-[#F9FAF8]"
                >
                  <span className="flex items-center gap-4">
                    <Mail size={18} className="text-[#1B3B28]" />
                    <span>
                      <span className="block text-[10px] uppercase tracking-[0.3em] text-[#8EA696]">E-mail</span>
                      <span className="text-base text-[#0A0A0A]">{salon.email}</span>
                    </span>
                  </span>
                  <ArrowUpRight size={18} className="text-[#11261A] transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" />
                </a>
                <a
                  href={BOOKING_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="contact-book-online-button"
                  className="group flex items-center justify-between border-2 border-dashed border-[#1B3B28]/30 px-6 py-5 transition-all duration-300 hover:border-[#11261A] hover:bg-[#F9FAF8]"
                >
                  <span>
                    <span className="block text-[10px] uppercase tracking-[0.3em] text-[#8EA696]">Prefer online?</span>
                    <span className="text-base text-[#0A0A0A]">Book instantly online</span>
                  </span>
                  <ArrowUpRight size={18} className="text-[#11261A] transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" />
                </a>
                <a
                  href="https://share.google/loQbX71kZILHwidN1"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="contact-google-business-link"
                  className="group flex items-center justify-between border border-[#E5E7E3] px-6 py-5 transition-all duration-300 hover:border-[#11261A] hover:bg-[#F9FAF8]"
                >
                  <span className="flex items-center gap-4">
                    <svg viewBox="0 0 24 24" className="w-[18px] h-[18px] fill-[#1B3B28]" aria-hidden="true">
                      <path d="M12 2l2.9 6.2 6.6.8-4.9 4.6 1.3 6.6L12 17l-5.9 3.2 1.3-6.6L2.5 9l6.6-.8L12 2z" />
                    </svg>
                    <span>
                      <span className="block text-[10px] uppercase tracking-[0.3em] text-[#8EA696]">Google Business Profile</span>
                      <span className="text-base text-[#0A0A0A]">Reviews &amp; directions on Google</span>
                    </span>
                  </span>
                  <ArrowUpRight size={18} className="text-[#11261A] transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" />
                </a>
              </div>
            </FadeUp>
          </div>

          <div className="lg:col-span-7">
            <FadeUp delay={0.15} className="h-full">
              <div className="relative h-full min-h-[420px]">
                <div className="absolute -inset-2 border border-[#8EA696]/40 translate-x-4 translate-y-4 pointer-events-none" />
                <iframe
                  title="Salon Orgænics location map"
                  data-testid="contact-map"
                  src={MAP_URL}
                  className="w-full h-full min-h-[420px] border-0 grayscale-[35%] contrast-[1.05]"
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  allowFullScreen
                />
              </div>
            </FadeUp>
          </div>
        </div>
      </section>
      <PhotoMarquee />
    </div>
  );
}
